import { useState, useEffect } from 'react';
import { Loading } from '@evertrue/et-components';
import EverTrue from 'app';
import Proptypes from 'prop-types';
import SelectedContactsSource from 'apps/contact/sources/selected-contacts-source';
import SelectorMenu from 'components/controls/selector-menu';
import _ from 'underscore';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCES_PICKER_QUERY, BULK_ENROLL_MEMBERS_MUTATION } from '../utils/cadence-queries';

/**
 * Cadences 2.0 selector for the "Add to..." popover on the contacts list.
 * Fetches cadences from the et-cadences GraphQL API and uses bulkEnrollMembers
 * to add selected contacts. Mirrors JourneySelectorController's UX.
 */
const CadenceV2SelectorController = ({ contactIds = [], closePopover, allSelected, totalResultsCount }) => {
  const [cadences, setCadences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnrolling, setIsEnrolling] = useState(false);

  useEffect(() => {
    const currentUserId = EverTrue.store?.user?.get('id');
    cadenceGqlCall(CADENCES_PICKER_QUERY)
      .then((data) => {
        const items = (data.cadences || [])
          .filter((c) => {
            const status = (c.displayStatusLabel || '').toLowerCase();
            const type = (c.cadenceType || '').toLowerCase();
            if (c.isTemplate) return false;
            if (type === 'bulk_message') return false;
            if (status !== 'active' && status !== 'paused') return false;
            if (c.enrollmentLocked) return false;
            if (currentUserId && String(c.ownerUserId) !== String(currentUserId)) return false;
            return true;
          })
          .map((c) => ({ label: c.name, value: c.id, cadenceType: c.cadenceType }));
        setCadences(items);
      })
      .catch(() => setCadences([]))
      .finally(() => setLoading(false));
  }, []);

  const handleCadenceClick = async (item) => {
    if (isEnrolling) return;
    setIsEnrolling(true);
    try {
      const { label, value: cadenceId } = item;
      const data = await cadenceGqlCall(BULK_ENROLL_MEMBERS_MUTATION, {
        input: { cadenceId: String(cadenceId), contactIds: contactIds.map(Number) },
      });
      const result = data.bulkEnrollMembers;
      if (result?.success) {
        EverTrue.Alert.success(`Adding ${contactIds.length} contact(s) to "${label}"`);
      } else {
        EverTrue.Alert.error(result?.message || `Failed to add contacts to "${label}"`);
      }
      SelectedContactsSource.clearAllSelectedState();
      closePopover();
    } catch (error) {
      EverTrue.Alert.error('Failed to enroll contacts — please try again');
    } finally {
      setIsEnrolling(false);
    }
  };

  if (totalResultsCount > 200 && allSelected) {
    return (
      <div className="trip-selector--empty">
        Unable to add more than 200 contacts to a Cadence at one time. Please narrow your selection.
      </div>
    );
  }

  return (
    <>
      <SelectorMenu
        isDisabled={(!contactIds?.length && !allSelected) || isEnrolling}
        label="Your Cadences 2.0"
        options={[
          {
            value: 'cadences-v2',
            label: 'Your Cadences 2.0',
            items: _.chain(cadences)
              .map((c) => ({ value: c.value, label: c.label }))
              .sortBy('label')
              .value(),
          },
        ]}
        onSelect={handleCadenceClick}
        searchPlaceholder="Search cadence..."
        onCreate={() => {}}
        showCreateLink={false}
      />
      {loading && <Loading size="small" />}
    </>
  );
};

CadenceV2SelectorController.propTypes = {
  contactIds: Proptypes.array.isRequired,
  closePopover: Proptypes.func.isRequired,
  allSelected: Proptypes.bool,
  totalResultsCount: Proptypes.number,
};

export default CadenceV2SelectorController;
